'use client';

import { useState } from 'react';
import Image from 'next/image'

interface Video {
  id: string;
  title: string;
  thumbnail: string;
  embedUrl: string;
}

interface VideoTabProps {
  videos: Video[];
}

// Function to check if URL is a YouTube link
const isYouTubeUrl = (url: string): boolean => {
  return url.includes('youtube.com') || url.includes('youtu.be')
}

// Function to get YouTube thumbnail URL
const getYouTubeThumbnailUrl = (videoId: string, quality: 'default' | 'hq' | 'maxres' = 'maxres'): string => {
  if (quality === 'default') {
    return `https://img.youtube.com/vi/${videoId}/0.jpg`
  } else if (quality === 'hq') {
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
  } else {
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
  }
}

export default function VideoTab({ videos }: VideoTabProps) {
  // Video playing state - tracks which video is currently playing (only one at a time)
  const [playingVideoId, setPlayingVideoId] = useState<string | null>(null);

  // Function to handle video play - stops other videos and starts the selected one
  const handleVideoPlay = (videoId: string) => {
    setPlayingVideoId(videoId);
  };

  // Function to get YouTube video ID from processed embed URL
  const getVideoIdFromEmbedUrl = (embedUrl: string): string | null => {
    const match = embedUrl.match(/\/embed\/([^?]+)/);
    return match ? match[1] : null;
  };

  if (videos.length === 0) {
    return (
      <div className='text-center py-12'>
        <p className='text-gray-500 text-lg'>No videos available at the moment.</p>
      </div>
    );
  }

  return (
    <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
      {videos.map((video) => {
        const isPlaying = playingVideoId === video.id;
        const videoId = getVideoIdFromEmbedUrl(video.embedUrl);
        
        return (
          <div key={video.id} className='bg-white rounded-lg shadow-lg overflow-hidden'>
            <div className='aspect-video relative'>
              {isPlaying ? (
                // Show iframe when playing
                <iframe
                  src={video.embedUrl}
                  title={video.title}
                  className='w-full h-full absolute inset-0'
                  frameBorder='0'
                  allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                  allowFullScreen
                ></iframe>
              ) : (
                // Show thumbnail when not playing
                <div className='relative w-full h-full cursor-pointer group' onClick={() => handleVideoPlay(video.id)}>
                  {/* Thumbnail Image */}
                  {videoId && isYouTubeUrl(video.embedUrl) ? (
                    <Image
                      src={getYouTubeThumbnailUrl(videoId)}
                      alt={video.title}
                      fill
                      className='object-cover group-hover:scale-105 transition-transform duration-300'
                      unoptimized
                      priority={false}
                    />
                  ) : (
                    <div className='w-full h-full bg-gray-200 flex items-center justify-center'>
                      <span className='text-gray-500'>Video Preview</span>
                    </div>
                  )}
                  
                  {/* Play Button Overlay */}
                  <div className='absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors'>
                    <div className='group-hover:scale-110 transition-all duration-300'>
                      {/* YouTube Play Icon */}
                      <svg width="68" height="48" viewBox="0 0 68 48" className="drop-shadow-lg md:w-[68px] w-[40px]">
                        <path className="fill-red-600 group-hover:fill-red-700" d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z"></path>
                        <path className="fill-white" d="M 45,24 27,14 27,34"></path>
                      </svg>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className='p-4'>
              <h3 className='font-brandon text-brown text-xl'>
                {video.title}
              </h3>
            </div>
          </div>
        );
      })}
    </div>
  );
} 